import { Maybe } from '@varo/graphql-schema-types';
import { useFocusEffect, useNavigation } from 'expo-router';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import {
  ErrorScreen,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';

export enum VlocStepName {
  SOFT_CREDIT_CHECK = 'soft_credit_check',
  HARD_CREDIT_CHECK = 'hard_credit_check',
  VLOC_DISBURSEMENT_CONFIRMATION = 'vloc_disbursement_confirmation',
  VLOC_GENERAL = 'vloc_general',
  USER_INFO = 'user_info',
  VLOC_OFFER = 'vloc_offer',
  VLOC_QUALIFICATION_DETAILS = 'vloc_qualification_details',
  VLOC_HOME_PAGE = 'vloc_home_page',
  VLOC_REPAYMENT_CONFIRMATION = 'vloc_repayment_confirmation',
  VLOC_REPAYMENT_AMOUNT = 'vloc_repayment_amount',
  VLOC_AUTOPAY = 'vloc_autopay',
  VLOC_MANAGE_ACCOUNT = 'vloc_manage_account',
  VLOC_TRANSACTION_HISTORY = 'vloc_transaction_history',
  VLOC_HISTORY_DETAILS = 'vloc_history_details',
}

export interface LineOfCreditErrorScreenComponentProps {
  screenAnalyticsId: string;
  trackingAnalyticsId: string;
  trackingCustomAttributes: Record<string, string>;
  insetBottomSafeArea?: boolean;
  buttonPressKey?: Maybe<string | undefined>;
}

const LineOfCreditErrorScreenComponent = ({
  screenAnalyticsId,
  trackingAnalyticsId,
  trackingCustomAttributes,
  buttonPressKey,
  insetBottomSafeArea = false,
}: LineOfCreditErrorScreenComponentProps) => {
  const { t: translatedText } = useTranslation('vloc');
  const { analyticsEvents } = useAnalyticsConfiguration();

  useFocusEffect(
    useCallback(() => {
      if (trackingAnalyticsId) {
        void analyticsEvents.logPressableEventFromConfig(
          trackingAnalyticsId,
          trackingCustomAttributes,
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackingAnalyticsId]),
  );

  useViewDidFocusScreenAnalyticsEvent({
    key: screenAnalyticsId,
  });

  const navigation = useNavigation();

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  useEffect(() => {
    navigation.setOptions({ gestureEnabled: false, headerBackVisible: false });
  }, [navigation]);

  return (
    <ErrorScreen
      messageHeadline={translatedText('error.title')}
      messageBody={translatedText('error.subTitle')}
      screenType={ErrorScreenType.Error}
      insetBottomSafeArea={insetBottomSafeArea}
      primaryButton={{
        analytics: {
          analyticsId: buttonPressKey ? buttonPressKey : 'errorButtonPress',
          customAttributes: { cta: 'Done' },
        },
        title: translatedText('error.button'),
        onPress: () => {
          goHome();
        },
      }}
    />
  );
};

export default LineOfCreditErrorScreenComponent;
